/**
 * Extraction Preview Component
 *
 * Displays extracted data with confidence indicators and
 * allows manual correction of low-confidence fields.
 */

import { t } from '../../i18n/translations.js';
import { renderConfidenceIndicator, renderConfidenceBadge, getConfidenceLevel } from './ConfidenceIndicator.js';

/**
 * Get auth token for API requests
 */
function getAuthToken() {
  // Get token from auth service storage (stored as JSON with accessToken property)
  try {
    const authData = localStorage.getItem('auth');
    if (authData) {
      const { accessToken, expiresAt } = JSON.parse(authData);
      // Check if token is not expired
      if (expiresAt && new Date(expiresAt) > new Date()) {
        return accessToken;
      }
    }
  } catch (e) {
    console.warn('Failed to get auth token:', e);
  }
  return null;
}

/**
 * Component state
 */
const state = {
  documentId: null,
  extraction: null,
  loading: true,
  saving: false,
  editingField: null,
  corrections: {},
  error: null,
};

/**
 * Fetch extraction data
 */
async function fetchExtraction(documentId) {
  console.log('[ExtractionPreview] fetchExtraction called for:', documentId);
  state.documentId = documentId;
  state.loading = true;
  state.error = null;
  render();

  try {
    const url = `/api/v1/documents/${documentId}/extraction`;
    console.log('[ExtractionPreview] Fetching:', url);

    const token = getAuthToken();
    if (!token) {
      throw new Error('Not authenticated. Please log in again.');
    }

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    console.log('[ExtractionPreview] Response status:', response.status);

    if (!response.ok) {
      throw new Error(`Failed to load extraction: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[ExtractionPreview] Response data:', data);
    state.extraction = data;
  } catch (error) {
    console.error('[ExtractionPreview] Error fetching extraction:', error);
    state.error = error.message;
  } finally {
    state.loading = false;
    console.log('[ExtractionPreview] Calling render, state:', { loading: state.loading, error: state.error, hasExtraction: !!state.extraction });
    render();
  }
}

/**
 * Save corrections
 */
async function saveCorrections() {
  if (Object.keys(state.corrections).length === 0) {
    alert('No corrections to save');
    return;
  }

  state.saving = true;
  render();

  try {
    const response = await fetch(`/api/v1/documents/${state.documentId}/extraction`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify({
        corrections: Object.entries(state.corrections).map(([fieldName, correctedValue]) => ({
          fieldName,
          correctedValue,
        })),
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to save corrections: ${response.statusText}`);
    }

    const result = await response.json();
    
    // Update local state
    result.updatedFields.forEach(updatedField => {
      const field = state.extraction.fields.find(f => f.fieldName === updatedField.fieldName);
      if (field) {
        field.value = updatedField.value;
        field.confidence = updatedField.confidence;
        field.isCorrected = true;
      }
    });

    state.extraction.overallConfidence = result.newConfidence;
    state.corrections = {};
    state.editingField = null;

    alert(`${result.correctionsApplied} corrections saved successfully!`);
  } catch (error) {
    console.error('Error saving corrections:', error);
    alert(`Error: ${error.message}`);
  } finally {
    state.saving = false;
    render();
  }
}

/**
 * Start editing a field
 */
function startEditing(fieldName) {
  state.editingField = fieldName;
  render();

  // Focus the input after render
  setTimeout(() => {
    const input = document.getElementById(`edit-${fieldName}`);
    if (input) {
      input.focus();
      input.select();
    }
  }, 0);
}

/**
 * Save field edit
 */
function saveFieldEdit(fieldName, value) {
  state.corrections[fieldName] = value;
  state.editingField = null;
  render();
}

/**
 * Cancel field edit
 */
function cancelFieldEdit() {
  state.editingField = null;
  render();
}

/**
 * Get current field value (with any pending corrections)
 */
function getFieldValue(field) {
  return state.corrections[field.fieldName] ?? field.value;
}

/**
 * Format field name for display
 */
function formatFieldName(fieldName) {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/[._]/g, ' ')
    .replace(/\[(\d+)\]/g, ' $1')
    .trim()
    .replace(/^\w/, c => c.toUpperCase());
}

/**
 * Render a single field row
 */
function renderField(field) {
  const { fieldName, value, confidence, sourceText, isCorrected } = field;
  const isEditing = state.editingField === fieldName;
  const hasPendingCorrection = state.corrections[fieldName] !== undefined;
  const displayValue = getFieldValue(field);
  const levelInfo = getConfidenceLevel(confidence);
  const needsReview = confidence < 0.8 && !isCorrected;

  return `
    <div class="extraction-field ${needsReview ? 'needs-review' : ''} ${hasPendingCorrection ? 'has-correction' : ''} ${isCorrected ? 'is-corrected' : ''}">
      <div class="field-header">
        <span class="field-name">${formatFieldName(fieldName)}</span>
        <div class="field-confidence">
          ${renderConfidenceBadge(confidence)}
          ${isCorrected ? '<span class="corrected-badge">Corrected</span>' : ''}
        </div>
      </div>
      
      <div class="field-content">
        ${isEditing ? `
          <div class="field-edit">
            <input 
              type="text" 
              id="edit-${fieldName}"
              class="field-edit-input"
              value="${displayValue || ''}"
              data-field="${fieldName}"
            />
            <div class="field-edit-actions">
              <button class="btn btn-sm btn-primary" data-action="save-edit" data-field="${fieldName}">
                Save
              </button>
              <button class="btn btn-sm btn-secondary" data-action="cancel-edit">
                Cancel
              </button>
            </div>
          </div>
        ` : `
          <div class="field-value-container">
            <span class="field-value ${!displayValue ? 'empty' : ''}">${displayValue || '(empty)'}</span>
            <button class="field-edit-btn" data-action="edit" data-field="${fieldName}" title="Edit value">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" stroke-width="2"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
          </div>
        `}
        ${sourceText ? `<div class="field-source">Source: "${sourceText}"</div>` : ''}
      </div>
    </div>
  `;
}

/**
 * Render line items table
 */
function renderLineItems(lineItems) {
  if (!lineItems || lineItems.length === 0) {
    return '';
  }

  return `
    <div class="extraction-section">
      <h3 class="section-title">Line Items</h3>
      <div class="line-items-table-container">
        <table class="line-items-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Amount</th>
              <th>Confidence</th>
            </tr>
          </thead>
          <tbody>
            ${lineItems.map((item, index) => `
              <tr>
                <td>${item.description || '-'}</td>
                <td class="numeric">${item.quantity ?? '-'}</td>
                <td class="numeric">${item.unitPrice || item.unit_price ? `$${parseFloat(item.unitPrice || item.unit_price).toFixed(2)}` : '-'}</td>
                <td class="numeric">${item.amount ? `$${parseFloat(item.amount).toFixed(2)}` : '-'}</td>
                <td>${renderConfidenceBadge(item.confidence || 0.8)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

/**
 * Render loading state
 */
function renderLoading() {
  return `
    <div class="extraction-loading">
      <div class="loading-spinner"></div>
      <p>Loading extraction data...</p>
    </div>
  `;
}

/**
 * Render error state
 */
function renderError() {
  return `
    <div class="extraction-error">
      <svg class="error-icon" viewBox="0 0 24 24" width="48" height="48">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
        <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <circle cx="12" cy="16" r="1" fill="currentColor"/>
      </svg>
      <h3>Error Loading Extraction</h3>
      <p>${state.error}</p>
      <button class="btn btn-primary" data-action="retry">Retry</button>
    </div>
  `;
}

/**
 * Main render function
 */
function render() {
  console.log('[ExtractionPreview] render() called');
  const container = document.getElementById('extraction-preview');
  console.log('[ExtractionPreview] Container found:', !!container, container?.id);
  if (!container) {
    console.error('[ExtractionPreview] Container not found! Cannot render.');
    return;
  }

  if (state.loading) {
    container.innerHTML = renderLoading();
    return;
  }

  if (state.error || !state.extraction) {
    console.log('[ExtractionPreview] Rendering error state:', { error: state.error, hasExtraction: !!state.extraction });
    container.innerHTML = renderError();
    attachEventListeners();
    return;
  }

  console.log('[ExtractionPreview] Rendering extraction data');
  const { extraction } = state;
  console.log('[ExtractionPreview] Extraction object:', extraction);
  console.log('[ExtractionPreview] Fields:', extraction?.fields);

  if (!extraction.fields || !Array.isArray(extraction.fields)) {
    console.error('[ExtractionPreview] Invalid extraction data - fields is not an array:', extraction);
    state.error = 'Invalid extraction data received';
    container.innerHTML = renderError();
    attachEventListeners();
    return;
  }

  // Filter out internal fields that should not be visible to customers
  // Issues are recorded internally but hidden to prevent customer manipulation
  const internalFields = ['issues', 'issue', 'extractionIssues', 'extraction_issues', 'validationIssues', 'validation_issues'];
  const visibleFields = extraction.fields.filter(f =>
    !internalFields.some(internal => f.fieldName.toLowerCase().includes(internal.toLowerCase()))
  );

  const highConfidenceCount = visibleFields.filter(f => f.confidence >= 0.8).length;
  const lowConfidenceCount = visibleFields.filter(f => f.confidence < 0.8).length;
  const pendingCorrectionsCount = Object.keys(state.corrections).length;

  try {
    container.innerHTML = `
    <div class="extraction-preview-container">
      <!-- Header -->
      <div class="extraction-header">
        <div class="extraction-info">
          <h2 class="extraction-title">${extraction.filename}</h2>
          <div class="extraction-meta">
            <span class="extraction-type">${extraction.documentType || 'Document'}</span>
            <span class="extraction-status ${extraction.extractionStatus}">${extraction.extractionStatus}</span>
          </div>
        </div>
        
        <div class="extraction-confidence-summary">
          ${renderConfidenceIndicator({ 
            confidence: extraction.overallConfidence, 
            size: 'large',
            showBar: true 
          })}
        </div>
      </div>

      <!-- Stats Bar -->
      <div class="extraction-stats">
        <div class="stat-item">
          <span class="stat-value">${visibleFields.length}</span>
          <span class="stat-label">Fields Extracted</span>
        </div>
        <div class="stat-item confidence-high">
          <span class="stat-value">${highConfidenceCount}</span>
          <span class="stat-label">High Confidence</span>
        </div>
        <div class="stat-item ${lowConfidenceCount > 0 ? 'confidence-low' : ''}">
          <span class="stat-value">${lowConfidenceCount}</span>
          <span class="stat-label">Needs Review</span>
        </div>
        ${pendingCorrectionsCount > 0 ? `
          <div class="stat-item has-corrections">
            <span class="stat-value">${pendingCorrectionsCount}</span>
            <span class="stat-label">Pending Changes</span>
          </div>
        ` : ''}
      </div>

      <!-- Low Confidence Alert -->
      ${lowConfidenceCount > 0 ? `
        <div class="extraction-alert">
          <svg class="alert-icon" viewBox="0 0 24 24" width="20" height="20">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" fill="none" stroke="currentColor" stroke-width="2"/>
            <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" stroke-width="2"/>
            <circle cx="12" cy="17" r="1" fill="currentColor"/>
          </svg>
          <span>${lowConfidenceCount} field(s) have low confidence and may need manual review.</span>
        </div>
      ` : ''}

      <!-- Fields Section -->
      <div class="extraction-section">
        <h3 class="section-title">Extracted Fields</h3>
        <div class="fields-grid">
          ${visibleFields.map(f => renderField(f)).join('')}
        </div>
      </div>

      <!-- Line Items -->
      ${renderLineItems(extraction.lineItems)}

      <!-- Actions -->
      <div class="extraction-actions">
        ${pendingCorrectionsCount > 0 ? `
          <button class="btn btn-primary" data-action="save" ${state.saving ? 'disabled' : ''}>
            ${state.saving ? 'Saving...' : `Save ${pendingCorrectionsCount} Correction(s)`}
          </button>
        ` : ''}
      </div>
    </div>
  `;

    console.log('[ExtractionPreview] HTML successfully rendered');
    attachEventListeners();
  } catch (error) {
    console.error('[ExtractionPreview] Error rendering HTML:', error);
    state.error = 'Error rendering extraction preview: ' + error.message;
    container.innerHTML = renderError();
    attachEventListeners();
  }
}

/**
 * Attach event listeners
 */
function attachEventListeners() {
  // Edit buttons
  document.querySelectorAll('[data-action="edit"]').forEach(btn => {
    btn.addEventListener('click', () => {
      startEditing(btn.dataset.field);
    });
  });

  // Save edit buttons
  document.querySelectorAll('[data-action="save-edit"]').forEach(btn => {
    btn.addEventListener('click', () => {
      const fieldName = btn.dataset.field;
      const input = document.getElementById(`edit-${fieldName}`);
      if (input) {
        saveFieldEdit(fieldName, input.value);
      }
    });
  });

  // Cancel edit buttons
  document.querySelectorAll('[data-action="cancel-edit"]').forEach(btn => {
    btn.addEventListener('click', cancelFieldEdit);
  });

  // Save corrections button
  document.querySelectorAll('[data-action="save"]').forEach(btn => {
    btn.addEventListener('click', saveCorrections);
  });

  // Retry button
  document.querySelectorAll('[data-action="retry"]').forEach(btn => {
    btn.addEventListener('click', () => {
      if (state.documentId) {
        fetchExtraction(state.documentId);
      }
    });
  });

  // Handle Enter key in edit inputs
  document.querySelectorAll('.field-edit-input').forEach(input => {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        saveFieldEdit(input.dataset.field, input.value);
      } else if (e.key === 'Escape') {
        cancelFieldEdit();
      }
    });
  });
}

/**
 * Initialize with document ID
 */
function init(documentId) {
  if (documentId) {
    fetchExtraction(documentId);
  }
}

/**
 * Create extraction preview component
 */
export function createExtractionPreview(documentId) {
  const container = document.createElement('div');
  container.id = 'extraction-preview';
  container.className = 'extraction-preview';

  // Show loading state immediately
  container.innerHTML = `
    <div class="extraction-loading">
      <div class="loading-spinner"></div>
      <p>Loading extraction data...</p>
    </div>
  `;

  if (documentId) {
    // Use requestAnimationFrame to ensure container is in DOM before fetching
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        console.log('[ExtractionPreview] Starting fetch for document:', documentId);
        fetchExtraction(documentId);
      });
    });
  }

  return container;
}

export default {
  init,
  createExtractionPreview,
  fetchExtraction,
  saveCorrections,
  getState: () => state,
};
